import Navbar from '../components/Navbar';
import { useLanguage } from '../contexts/LanguageContext';
import { BookOpen, Video, FileText, Globe, BookMarked, Download, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';

const ResourcesPage = () => {
  const { language } = useLanguage();

  const getText = (key) => {
    const texts = {
      title: {
        fr: 'Ressources et conseils',
        en: 'Resources and Tips',
        uk: 'Ресурси та поради',
        ru: 'Ресурсы и советы',
        it: 'Risorse e consigli',
        ro: 'Resurse și sfaturi',
        ka: 'რესურსები და რჩევები'
      },
      subtitle: {
        fr: 'Guides, articles et informations pour votre parcours de bien-être',
        en: 'Guides, articles and information for your wellness journey',
        uk: 'Посібники, статті та інформація для вашого шляху до благополуччя',
        ru: 'Руководства, статьи и информация для вашего пути к благополучию',
        it: 'Guide, articoli e informazioni per il tuo percorso di benessere',
        ro: 'Ghiduri, articole și informații pentru călătoria ta spre bunăstare',
        ka: 'გზამკვლევები, სტატიები და ინფორმაცია თქვენი კეთილდღეობის გზისთვის'
      },
      guides: {
        fr: 'Guides pratiques',
        en: 'Practical Guides',
        uk: 'Практичні посібники',
        ru: 'Практические руководства',
        it: 'Guide pratiche',
        ro: 'Ghiduri practice',
        ka: 'პრაქტიკული გზამკვლევები'
      },
      articles: {
        fr: 'Articles',
        en: 'Articles',
        uk: 'Статті',
        ru: 'Статьи',
        it: 'Articoli',
        ro: 'Articole',
        ka: 'სტატიები'
      },
      glossary: {
        fr: 'Glossaire médical',
        en: 'Medical Glossary',
        uk: 'Медичний глосарій',
        ru: 'Медицинский глоссарий',
        it: 'Glossario medico',
        ro: 'Glosar medical',
        ka: 'სამედიცინო ლექსიკონი'
      },
      culturalInfo: {
        fr: 'Différences culturelles',
        en: 'Cultural Differences',
        uk: 'Культурні відмінності',
        ru: 'Культурные различия',
        it: 'Differenze culturali',
        ro: 'Diferențe culturale',
        ka: 'კულტურული განსხვავებები'
      },
      readMore: {
        fr: 'Lire la suite',
        en: 'Read more',
        uk: 'Читати далі',
        ru: 'Читать далее',
        it: 'Leggi di più',
        ro: 'Citește mai mult',
        ka: 'წაიკითხეთ მეტი'
      },
      download: {
        fr: 'Télécharger',
        en: 'Download',
        uk: 'Завантажити',
        ru: 'Скачать',
        it: 'Scarica',
        ro: 'Descarcă',
        ka: 'ჩამოტვირთვა'
      },
      comingSoon: {
        fr: 'Bientôt disponible',
        en: 'Coming soon',
        uk: 'Незабаром',
        ru: 'Скоро',
        it: 'Prossimamente',
        ro: 'În curând',
        ka: 'მალე'
      }
    };
    return texts[key]?.[language] || texts[key]?.['fr'] || key;
  };

  const guides = [
    {
      icon: BookMarked,
      title: {
        fr: 'Guide du patient international',
        en: 'International Patient Guide',
        uk: 'Посібник міжнародного пацієнта',
        ru: 'Руководство международного пациента',
        it: 'Guida del paziente internazionale',
        ro: 'Ghidul pacientului internațional',
        ka: 'საერთაშორისო პაციენტის გზამკვლევი'
      },
      description: {
        fr: 'Tout ce que vous devez savoir avant de consulter un praticien à l\'étranger',
        en: 'Everything you need to know before consulting a practitioner abroad',
        uk: 'Все, що потрібно знати перед консультацією зі спеціалістом за кордоном',
        ru: 'Всё, что нужно знать перед консультацией со специалистом за рубежом',
        it: 'Tutto ciò che devi sapere prima di consultare un professionista all\'estero',
        ro: 'Tot ce trebuie să știi înainte de a consulta un specialist în străinătate',
        ka: 'ყველაფერი რაც უნდა იცოდეთ საზღვარგარეთ სპეციალისტთან კონსულტაციამდე'
      },
      color: 'bg-blue-50 text-blue-600'
    },
    {
      icon: Video,
      title: {
        fr: 'Préparer une consultation vidéo',
        en: 'Prepare for a Video Consultation',
        uk: 'Підготовка до відео-консультації',
        ru: 'Подготовка к видео-консультации',
        it: 'Prepararsi per una consulenza video',
        ro: 'Pregătirea pentru o consultație video',
        ka: 'მოემზადეთ ვიდეო კონსულტაციისთვის'
      },
      description: {
        fr: 'Conseils pour une consultation en visioconférence réussie',
        en: 'Tips for a successful video consultation',
        uk: 'Поради для успішної відео-консультації',
        ru: 'Советы для успешной видео-консультации',
        it: 'Consigli per una consulenza video di successo',
        ro: 'Sfaturi pentru o consultație video reușită',
        ka: 'რჩევები წარმატებული ვიდეო კონსულტაციისთვის'
      },
      color: 'bg-purple-50 text-purple-600'
    },
    {
      icon: Globe,
      title: {
        fr: 'Médecines douces dans le monde',
        en: 'Natural Medicine Worldwide',
        uk: 'Натуральна медицина у світі',
        ru: 'Натуральная медицина в мире',
        it: 'Medicina naturale nel mondo',
        ro: 'Medicina naturală în lume',
        ka: 'ბუნებრივი მედიცინა მსოფლიოში'
      },
      description: {
        fr: 'Découvrez les pratiques de bien-être à travers les cultures',
        en: 'Discover wellness practices across cultures',
        uk: 'Відкрийте практики благополуччя різних культур',
        ru: 'Откройте практики благополучия разных культур',
        it: 'Scopri le pratiche di benessere attraverso le culture',
        ro: 'Descoperă practicile de bunăstare din diferite culturi',
        ka: 'აღმოაჩინეთ კეთილდღეობის პრაქტიკები სხვადასხვა კულტურაში'
      },
      color: 'bg-green-50 text-green-600'
    }
  ];

  const glossaryTerms = [
    {
      term: {
        fr: 'Naturopathie',
        en: 'Naturopathy',
        uk: 'Натуропатія',
        ru: 'Натуропатия',
        it: 'Naturopatia',
        ro: 'Naturopatie',
        ka: 'ნატუროპათია'
      },
      definition: {
        fr: 'Approche holistique de la santé utilisant des remèdes naturels',
        en: 'Holistic health approach using natural remedies',
        uk: 'Холістичний підхід до здоров\'я з використанням природних засобів',
        ru: 'Холистический подход к здоровью с использованием природных средств',
        it: 'Approccio olistico alla salute utilizzando rimedi naturali',
        ro: 'Abordare holistică a sănătății folosind remedii naturale',
        ka: 'ჰოლისტიკური მიდგომა ჯანმრთელობისადმი ბუნებრივი საშუალებების გამოყენებით'
      }
    },
    {
      term: {
        fr: 'Sophrologie',
        en: 'Sophrology',
        uk: 'Софрологія',
        ru: 'Софрология',
        it: 'Sofrologia',
        ro: 'Sofrologie',
        ka: 'სოფროლოგია'
      },
      definition: {
        fr: 'Technique de relaxation dynamique combinant respiration et méditation',
        en: 'Dynamic relaxation technique combining breathing and meditation',
        uk: 'Техніка динамічної релаксації, що поєднує дихання та медитацію',
        ru: 'Техника динамической релаксации, сочетающая дыхание и медитацию',
        it: 'Tecnica di rilassamento dinamico che combina respirazione e meditazione',
        ro: 'Tehnică de relaxare dinamică ce combină respirația și meditația',
        ka: 'დინამიური რელაქსაციის ტექნიკა, რომელიც აერთიანებს სუნთქვასა და მედიტაციას'
      }
    },
    {
      term: {
        fr: 'Ostéopathie',
        en: 'Osteopathy',
        uk: 'Остеопатія',
        ru: 'Остеопатия',
        it: 'Osteopatia',
        ro: 'Osteopatie',
        ka: 'ოსტეოპათია'
      },
      definition: {
        fr: 'Thérapie manuelle traitant les troubles musculo-squelettiques',
        en: 'Manual therapy treating musculoskeletal disorders',
        uk: 'Мануальна терапія для лікування м\'язово-скелетних розладів',
        ru: 'Мануальная терапия для лечения мышечно-скелетных расстройств',
        it: 'Terapia manuale per il trattamento dei disturbi muscolo-scheletrici',
        ro: 'Terapie manuală pentru tratarea tulburărilor musculo-scheletice',
        ka: 'მექანიკური თერაპია კუნთ-ჩონჩხის დარღვევების სამკურნალოდ'
      }
    },
    {
      term: {
        fr: 'Hypnothérapie',
        en: 'Hypnotherapy',
        uk: 'Гіпнотерапія',
        ru: 'Гипнотерапия',
        it: 'Ipnoterapia',
        ro: 'Hipnoterapie',
        ka: 'ჰიპნოთერაპია'
      },
      definition: {
        fr: 'Utilisation de l\'hypnose à des fins thérapeutiques',
        en: 'Use of hypnosis for therapeutic purposes',
        uk: 'Використання гіпнозу в терапевтичних цілях',
        ru: 'Использование гипноза в терапевтических целях',
        it: 'Uso dell\'ipnosi a scopo terapeutico',
        ro: 'Utilizarea hipnozei în scopuri terapeutice',
        ka: 'ჰიპნოზის გამოყენება თერაპიული მიზნებისთვის'
      }
    }
  ];

  const culturalTips = [
    {
      region: { fr: 'Europe de l\'Ouest', en: 'Western Europe', uk: 'Західна Європа', ru: 'Западная Европа', it: 'Europa occidentale', ro: 'Europa de Vest', ka: 'დასავლეთ ევროპა' },
      tips: {
        fr: 'Approche médicale structurée, importance de la ponctualité, confidentialité respectée',
        en: 'Structured medical approach, importance of punctuality, privacy respected',
        uk: 'Структурований медичний підхід, важливість пунктуальності, конфіденційність',
        ru: 'Структурированный медицинский подход, важность пунктуальности, конфиденциальность',
        it: 'Approccio medico strutturato, importanza della puntualità, privacy rispettata',
        ro: 'Abordare medicală structurată, importanța punctualității, confidențialitate respectată',
        ka: 'სტრუქტურირებული სამედიცინო მიდგომა, პუნქტუალურობის მნიშვნელობა, კონფიდენციალურობის დაცვა'
      }
    },
    {
      region: { fr: 'Europe de l\'Est', en: 'Eastern Europe', uk: 'Східна Європа', ru: 'Восточная Европа', it: 'Europa dell\'Est', ro: 'Europa de Est', ka: 'აღმოსავლეთ ევროპა' },
      tips: {
        fr: 'Relations patient-praticien chaleureuses, médecine traditionnelle valorisée',
        en: 'Warm patient-practitioner relationships, traditional medicine valued',
        uk: 'Теплі відносини пацієнт-спеціаліст, цінується традиційна медицина',
        ru: 'Тёплые отношения пациент-специалист, ценится традиционная медицина',
        it: 'Relazioni calde paziente-professionista, medicina tradizionale valorizzata',
        ro: 'Relații calde pacient-specialist, medicina tradițională apreciată',
        ka: 'თბილი პაციენტ-სპეციალისტის ურთიერთობები, ტრადიციული მედიცინის დაფასება'
      }
    }
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="resources-page">
      <Navbar />
      
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-purple-50 to-blue-50 py-16">
        <div className="container-app">
          <div className="max-w-3xl mx-auto text-center">
            <BookOpen className="w-16 h-16 text-purple-500 mx-auto mb-6" />
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-foreground mb-4">
              {getText('title')}
            </h1>
            <p className="text-lg text-muted-foreground">
              {getText('subtitle')}
            </p>
          </div>
        </div>
      </div>

      {/* Guides Section */}
      <div className="container-app py-12">
        <h2 className="font-heading text-2xl font-bold mb-8">{getText('guides')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {guides.map((guide, index) => (
            <div key={index} className="bg-white rounded-2xl p-6 border border-sand-200 shadow-soft hover:shadow-md transition-all group">
              <div className={`w-12 h-12 rounded-xl ${guide.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                <guide.icon className="w-6 h-6" />
              </div>
              <h3 className="font-heading text-lg font-semibold mb-2">
                {guide.title[language] || guide.title.fr}
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                {guide.description[language] || guide.description.fr}
              </p>
              <Button variant="outline" size="sm" className="w-full" disabled>
                {getText('comingSoon')}
              </Button>
            </div>
          ))}
        </div>
      </div>

      {/* Glossary Section */}
      <div className="bg-sand-50 py-12">
        <div className="container-app">
          <h2 className="font-heading text-2xl font-bold mb-8 flex items-center gap-3">
            <FileText className="w-7 h-7 text-sage-500" />
            {getText('glossary')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {glossaryTerms.map((item, index) => (
              <div key={index} className="bg-white rounded-xl p-5 border border-sand-200">
                <h4 className="font-semibold text-sage-700 mb-1">
                  {item.term[language] || item.term.fr}
                </h4>
                <p className="text-sm text-muted-foreground">
                  {item.definition[language] || item.definition.fr}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Cultural Differences Section */}
      <div className="container-app py-12">
        <h2 className="font-heading text-2xl font-bold mb-8 flex items-center gap-3">
          <Globe className="w-7 h-7 text-blue-500" />
          {getText('culturalInfo')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {culturalTips.map((item, index) => (
            <div key={index} className="bg-white rounded-2xl p-6 border border-sand-200 shadow-soft">
              <h3 className="font-heading text-lg font-semibold mb-3 text-foreground">
                {item.region[language] || item.region.fr}
              </h3>
              <p className="text-muted-foreground">
                {item.tips[language] || item.tips.fr}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-sage-500 to-sage-600 py-12">
        <div className="container-app text-center">
          <h2 className="font-heading text-2xl font-bold text-white mb-4">
            {language === 'uk' ? 'Готові знайти свого спеціаліста?' : language === 'ru' ? 'Готовы найти своего специалиста?' : language === 'en' ? 'Ready to find your practitioner?' : 'Prêt à trouver votre praticien ?'}
          </h2>
          <Button asChild className="bg-white text-sage-600 hover:bg-sand-50">
            <Link to="/search">
              {language === 'uk' ? 'Пошук спеціаліста' : language === 'ru' ? 'Найти специалиста' : language === 'en' ? 'Search practitioners' : 'Rechercher un praticien'}
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ResourcesPage;
